import { create } from "zustand"
import { devtools, persist } from "zustand/middleware"
import { APP_ID } from "../utils/constants"
import { request } from "utils"
import { useAuthStore } from "./auth"
import { useProfileStore } from "./profile"

export type ProgramType = {
  id: string
  description: string
}

export type ProgramCategoryType = {
  id: string
  name: string
  programmes: ProgramType[]
}

export type ApplyForProgrammeCredentials = {
  programmeId: string
}

export type UploadRecordCredentials = {
  documentType: "OlevelResult" | "EducationalRecord" | "ProfilePicture"
  file: string | File
}

export type EducationalRecordCredentials = {
  name: string
  qualification: string
  grade: string
  startDate: string
  endDate: string
  resultPath: string
}
export type DeleteEducationalRecord = {
  educationalRecordId: string
}
export type EducationalRecord = {
  educationalRecId: string
  profileId: string
  name: string
  qualification: string
  grade: string
  startDate: string
  endDate: string
  resultPath: string
}

export type OLevelResultCredentials = {
  examName: string
  examNumber: string
  examYear: string
  resultPath?: string
  subjects?: {
    subjectId: string
    subjectName: string
    grade: string
  }[]
}

export type OLevelSubject = {
  examinationId: string
  subjectId: string
  grade: string
}
export type DeleteOLevelSubject = {
  examinationResultId: string
}
export type OLevelResult = {
  resultId: string
  profileId: string
  examName: string
  examNumber: string
  examYear: string
  resultPath: string
  subjects: {
    examinationSubjectId: string
    subjectId: string
    subjectName: string
    grade: string
  }[]
}
export type DeleteOLevelResultCredentials = {
  resultId: string
}

export type EmploymentRecordCredentials = {
  employerName: string
  designation: string
  startDate: string
  endDate: string
}
export type EmploymentRecord = {
  employmentRecId: string
  profileId: string
  employerName: string
  designation: string
  startDate: string
  endDate: string
}
export type DeleteEmploymentRecordCredentials = {
  employmentRecordId: string
}
export type SubjectInterface = {
  name: string
  subjectId: string
}

interface ApplicationState {
  programCategories: ProgramCategoryType[]
  qualifications: string[]
  dateRanges: {
    startDate: string[]
    endDate: string[]
  }
  educationalRecords: EducationalRecord[]
  oLevelResults: OLevelResult[]
  employmentRecords: EmploymentRecord[]
  subjects: SubjectInterface[]
  grades: string[]
}

interface ApplicationMethods {
  getProgrammes: () => Promise<any>
  applyForProgramme: (credentials: ApplyForProgrammeCredentials) => Promise<any>
  getQualification: () => Promise<any>
  getDateRange: () => Promise<any>
  uploadFile: (credentials: FormData) => Promise<any>

  getEducationalRecords: () => Promise<any>
  addEducationalRecord: (
    credentials: EducationalRecordCredentials
  ) => Promise<any>
  completeEducationalRecord: (credentials: {
    complete: boolean
  }) => Promise<any>
  deleteEducationalRecord: (
    credentials: DeleteEducationalRecord
  ) => Promise<any>

  getSubjects: () => Promise<any>
  getGrades: () => Promise<any>
  getPrintRecieptUrl: () => Promise<any>

  getOLevelResults: () => Promise<any>
  addOLevelResult: (credentials: OLevelResultCredentials) => Promise<any>
  addOLevelSubject: (credentials: OLevelSubject) => Promise<any>
  deleteOLevelSubject: (credentials: DeleteOLevelSubject) => Promise<any>
  deleteOLevelResult: (
    credentials: DeleteOLevelResultCredentials
  ) => Promise<any>

  getEmploymentRecords: () => Promise<any>
  addEmploymentRecord: (
    credentials: EmploymentRecordCredentials
  ) => Promise<any>
  deleteEmploymentRecord: (
    credentials: DeleteEmploymentRecordCredentials
  ) => Promise<any>

  updateStage: (credentials: { currentStage: string }) => Promise<any>
}

export const useApplicationStore = create<
  ApplicationState & ApplicationMethods
>()(
  devtools(
    persist(
      (set, get) => ({
        programCategories: [],
        qualifications: [],
        dateRanges: {
          startDate: [],
          endDate: [],
        },
        educationalRecords: [],
        oLevelResults: [],
        employmentRecords: [],
        subjects: [],
        grades: [],
        getProgrammes: async () => {
          return await request
            .get({
              url: "/api/Application/get-programmes",
              headers: { ...useAuthStore.getState().getAuthHeader() },
            })
            .then((resp) => {
              set({
                programCategories: resp.data as ProgramCategoryType[],
              })
              return resp.data
            })
        },
        applyForProgramme: async (credentials) => {
          return request
            .post({
              url: "/api/Application/apply-for-programme",
              headers: { ...useAuthStore.getState().getAuthHeader() },
              body: credentials,
            })
            .then(async (resp) => {
              console.log(resp)
              if (resp === undefined) return
              if (resp.isSuccessful === true) {
                await useProfileStore.getState().getProfile()
              }
              return resp
            })
        },
        getQualification: async () => {
          return await request
            .get({
              url: "/api/Utility/get-qualification",
              headers: { ...useAuthStore.getState().getAuthHeader() },
            })
            .then((resp) => {
              if (resp.isSuccessful === true) {
                set({ qualifications: resp.data })
              }
            })
        },
        getDateRange: async () => {
          return await request
            .get({
              url: "/api/Utility/get-date-range",
              headers: { ...useAuthStore.getState().getAuthHeader() },
            })
            .then((resp) => {
              if (resp.isSuccessful === true) {
                set({ dateRanges: resp.data })
              }
            })
        },
        getPrintRecieptUrl: async () => {
          return await request.get({
            url: "/api/Printing/print-receipt",
            headers: { ...useAuthStore.getState().getAuthHeader() },
          })
          // .then((resp) => {
          //   if (resp.isSuccessful === true) {
          //     set({ dateRanges: resp.data })
          //   }
          // })
        },
        uploadFile: async (credentials) => {
          console.log(credentials)
          return await request.post({
            url: "/api/Utility/upload",
            headers: { ...useAuthStore.getState().getAuthHeader() },
            body: credentials,
          })
        },
        getEducationalRecords: async () => {
          return await request
            .get({
              url: "/api/Application/get-educational-record",
              headers: { ...useAuthStore.getState().getAuthHeader() },
            })
            .then((resp) => {
              if (resp.isSuccessful === true) {
                console.log(resp.data)
                set({
                  educationalRecords: resp.data,
                })
              }
            })
        },
        getSubjects: async () => {
          return await request
            .get({
              url: "/api/Utility/get-olevel-subjects",
              headers: { ...useAuthStore.getState().getAuthHeader() },
            })
            .then((resp) => {
              if (resp.isSuccessful === true) {
                set({
                  subjects: resp.data,
                })
              }
            })
        },
        getGrades: async () => {
          return await request
            .get({
              url: "/api/Utility/get-grades",
              headers: { ...useAuthStore.getState().getAuthHeader() },
            })
            .then((resp) => {
              if (resp.isSuccessful === true) {
                set({
                  grades: resp.data,
                })
              }
            })
        },
        addEducationalRecord: async (credentials) => {
          return await request
            .post({
              url: "/api/Application/add-educational-record",
              headers: { ...useAuthStore.getState().getAuthHeader() },
              body: credentials,
            })
            .then(async (resp) => {
              await get().getEducationalRecords()
              return resp
            })
        },
        completeEducationalRecord: async (credentials) => {
          return await request
            .post({
              url: "/api/Application/add-educational-record",
              headers: { ...useAuthStore.getState().getAuthHeader() },
              body: credentials,
            })
            .then(async (resp) => {
              return resp
            })
        },
        deleteEducationalRecord: async (credentials) => {
          return await request
            .delete({
              url: "/api/Application/remove-educational-record",
              headers: { ...useAuthStore.getState().getAuthHeader() },
              body: credentials,
            })
            .then(async (resp) => {
              await get().getEducationalRecords()
              return resp
            })
        },
        getOLevelResults: async () => {
          return await request
            .get({
              url: "/api/Application/get-olevel-results",
              headers: { ...useAuthStore.getState().getAuthHeader() },
            })
            .then((resp) => {
              if (resp.isSuccessful === true) {
                console.log(resp.data)
                set({
                  oLevelResults: resp.data,
                })
              }
            })
        },
        addOLevelResult: async (credentials) => {
          return await request
            .post({
              url: "/api/Application/add-olevel-result",
              headers: { ...useAuthStore.getState().getAuthHeader() },
              body: credentials,
            })
            .then(async (resp) => {
              await get().getOLevelResults()
              return resp
            })
        },
        addOLevelSubject: async (credentials) => {
          return await request
            .post({
              url: "/api/Application/add-examination-subject",
              headers: { ...useAuthStore.getState().getAuthHeader() },
              body: credentials,
            })
            .then(async (resp) => {
              await get().getOLevelResults()
              return resp
            })
        },
        deleteOLevelResult: async (credentials) => {
          return await request
            .delete({
              url: "/api/Application/remove-olevel-result",
              headers: { ...useAuthStore.getState().getAuthHeader() },
              body: credentials,
            })
            .then(async (resp) => {
              await get().getOLevelResults()
              return resp
            })
        },
        deleteOLevelSubject: async (credentials) => {
          return await request
            .delete({
              url: "/api/Application/remove-examination-subject",
              headers: { ...useAuthStore.getState().getAuthHeader() },
              body: credentials,
            })
            .then(async (resp) => {
              await get().getOLevelResults()
              return resp
            })
        },
        getEmploymentRecords: async () => {
          return await request
            .get({
              url: "/api/Application/get-employment-record",
              headers: { ...useAuthStore.getState().getAuthHeader() },
            })
            .then((resp) => {
              if (resp.isSuccessful === true) {
                console.log(resp.data)
                set({
                  employmentRecords: resp.data,
                })
              }
            })
        },
        addEmploymentRecord: async (credentials) => {
          return await request
            .post({
              url: "/api/Application/add-employment-record",
              headers: { ...useAuthStore.getState().getAuthHeader() },
              body: credentials,
            })
            .then(async (resp) => {
              await get().getEmploymentRecords()
              return resp
            })
        },
        deleteEmploymentRecord: async (credentials) => {
          return await request
            .delete({
              url: "/api/Application/remove-employment-record",
              headers: { ...useAuthStore.getState().getAuthHeader() },
              body: credentials,
            })
            .then(async (resp) => {
              await get().getEmploymentRecords()
              return resp
            })
        },
        updateStage: async (credentials) => {
          return await request
            .post({
              url: "/api/Application/update-stage",
              headers: { ...useAuthStore.getState().getAuthHeader() },
              body: credentials,
            })
            .then(async (resp) => {
              return resp
            })
        },
      }),
      {
        name: `${APP_ID}.application`,
      }
    )
  )
)

/*

{
  "examName": "string",
  "examNumber": "string",
  "examYear": "string",
  "subjects": [
    {
      "subjectId": "string",
      "subjectName": "string",
      "grade": "string"
    }
  ],
}

*/
